/**
 * Merge another person into this one: find them by name, email or phone,
 * see both side by side, then merge. This person is kept, and the
 * notification that follows offers Undo.
 *
 * The server refuses while stored people are off (409) or for someone
 * without link_people (403), and its reason is shown as it is.
 */
import { useEffect, useState } from 'react';
import {
  Alert, Button, Card, Group, Loader, Modal, SimpleGrid, Stack, Text, TextInput, UnstyledButton,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconSearch } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import {
  useMergePeople, usePeople, useUndoLink, type PersonBrief, type PersonDetail, type PersonSummary,
} from '../../api/people';
import { TOKENS } from '../../theme';
import { useVocab } from '../../vocab';
import { notifyWithUndo, personName, PersonBriefView } from './peopleShared';

const brief = (p: PersonSummary | PersonDetail): PersonBrief => ({
  id: p.id,
  name: p.name,
  email: p.email,
  phone: p.phone,
  last_seen: p.last_seen,
  submissions: 'submission_count' in p ? p.submission_count : p.submissions,
});

export function MergePersonModal({
  opened, person, onClose,
}: { opened: boolean; person: PersonDetail; onClose: () => void }) {
  const v = useVocab();
  const navigate = useNavigate();
  const merge = useMergePeople();
  const undo = useUndoLink();
  const [q, setQ] = useState('');
  const [debounced] = useDebouncedValue(q.trim(), 250);
  const [chosen, setChosen] = useState<PersonSummary | null>(null);
  const [error, setError] = useState('');
  const results = usePeople({ q: debounced, page_size: 8 }, opened && debounced.length >= 2);
  const hits = (results.data?.people ?? []).filter((p) => p.id !== person.id);

  useEffect(() => {
    if (opened) {
      setQ('');
      setChosen(null);
      setError('');
    }
  }, [opened]);

  async function confirm() {
    if (!chosen) return;
    setError('');
    try {
      const r = await merge.mutateAsync({ id: person.id, otherId: chosen.id });
      const name = personName(chosen);
      onClose();
      notifyWithUndo({
        title: 'Merged',
        message: `${name} is now part of ${personName(person)}.`,
        undo: async () => {
          try {
            const back = await undo.mutateAsync(r.link.id);
            notifications.show({ color: 'teal', title: 'Undone', message: `${name} is ${v.a('person')} of their own again.` });
            if (back.person_id !== person.id) navigate(`/people/${encodeURIComponent(back.person_id)}`);
          } catch (e) {
            notifications.show({ color: 'red', title: 'Not undone', message: (e as Error).message });
          }
        },
      });
      if (r.person_id !== person.id) navigate(`/people/${encodeURIComponent(r.person_id)}`, { replace: true });
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={chosen ? 'Check before merging' : `Merge another ${v.one('person')} into ${personName(person)}`}
      size="lg"
    >
      {!chosen ? (
        <Stack gap="sm">
          <TextInput
            data-autofocus
            aria-label={`Find ${v.a('person')}`}
            placeholder="Name, email or phone"
            leftSection={<IconSearch size={14} />}
            value={q}
            onChange={(e) => setQ(e.currentTarget.value)}
            rightSection={results.isFetching ? <Loader size={14} /> : null}
          />
          {debounced.length < 2 && (
            <Text size="sm" c="dimmed">{`Type at least two letters to find ${v.a('person')}.`}</Text>
          )}
          {debounced.length >= 2 && !results.isPending && hits.length === 0 && (
            <Text size="sm" c="dimmed">{`No other ${v.one('person')} matches.`}</Text>
          )}
          <Stack gap={4} data-testid="merge-results">
            {hits.map((p) => (
              <UnstyledButton
                key={p.id}
                onClick={() => setChosen(p)}
                p="xs"
                style={{ borderRadius: 6, border: `1px solid ${TOKENS.border}` }}
                aria-label={`Choose ${personName(p)}`}
              >
                <PersonBriefView person={brief(p)} v={v} />
              </UnstyledButton>
            ))}
          </Stack>
        </Stack>
      ) : (
        <Stack gap="md" data-testid="merge-confirm">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Card withBorder p="sm">
              <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>Kept</Text>
              <PersonBriefView person={brief(person)} v={v} />
            </Card>
            <Card withBorder p="sm">
              <Text size="xs" c="dimmed" fw={600} tt="uppercase" mb={4}>Merged in</Text>
              <PersonBriefView person={brief(chosen)} v={v} />
            </Card>
          </SimpleGrid>
          <Text size="sm">
            {`${personName(chosen)} and their ${v.count(chosen.submissions, 'lead')} join ${personName(person)}, who is kept. Nothing about the ${v.many('lead')} themselves changes. You can undo this.`}
          </Text>
          {error && <Alert color="red" variant="light" title="Not merged">{error}</Alert>}
          <Group justify="space-between">
            <Button variant="subtle" leftSection={<IconArrowLeft size={14} />} onClick={() => { setChosen(null); setError(''); }}>
              Back
            </Button>
            <Group gap="xs">
              <Button variant="subtle" onClick={onClose}>Cancel</Button>
              <Button onClick={confirm} loading={merge.isPending}>Merge</Button>
            </Group>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
